import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import cn from "classnames";

import { actions as mainActions } from "../../actions/main";
import { Logo } from "./logo";
import { DetailNavigation } from "./popups/detailNavigation";
import { Notification } from "./notification";
import { Hamburger } from "./hamburger";
import { Avatar } from "./avatar";
import { LanguageSwitcher } from "./languageSwitcher";
import MainSearch from "../mainSearch";
import MainSearchV2 from "../mainSearch/v2";
import {
  ACCOUNT_SETTINGS_V2,
  HEADER_SEARCH_V2_ENABLED,
  NOTIFICATIONS_V2,
  ORGANIZATION_LISTINGS_V2,
  EVENT_LISTINGS_V2,
  JOB_LISTINGS_V2,
  PAID_REFERRALS_V2,
  PAYMENTS_V2
} from "../../constants/featureFlags";
import styles from "./header.module.css";

const Header = ({ keycloak }) => {
  const location = useLocation();
  const [popup, setPopup] = useState(null);
  const dispatch = useDispatch();
  // communities
  const isActiveMobileMenu = useSelector(state => state.main.isActiveMobileMenu);
  const communities = useSelector(state => state.main.communities);
  const toggleMobileMenu = useCallback(isActive => dispatch(mainActions.toggleMobileMenu(isActive)), [dispatch]);
  const getCommunities = useCallback(() => dispatch(mainActions.getCommunities()), [dispatch]);

  // GROWTH-7718 / 7719 / 7720 / 7722 / 7922: Account Settings and the redesigned Notifications, Payments, Team and Paid Referrals pages share a
  // tablet and phone header (40px menu button, logo, no search on a phone).
  const isAccountSettings =
    ACCOUNT_SETTINGS_V2 &&
    (location.pathname === "/account" ||
      (NOTIFICATIONS_V2 && location.pathname === "/manage-notification") ||
      (PAYMENTS_V2 && location.pathname === "/payments") ||
      (PAID_REFERRALS_V2 && location.pathname === "/paid-referrals") ||
      (ORGANIZATION_LISTINGS_V2 && location.pathname === "/my-organizations") ||
      (EVENT_LISTINGS_V2 && location.pathname === "/my-events") ||
      (JOB_LISTINGS_V2 && location.pathname === "/my-jobs") ||
      location.pathname === "/teams");
  const isListingDetailPage = /^\/(profile|my-jobs|my-events|my-organizations)(\/|$)/.test(location.pathname);

  const handleOpenNewListing = () => {
    toggleMobileMenu(!isActiveMobileMenu);
  };

  useEffect(() => {
    setPopup(false);
  }, [location.search]);

  // The v1 search filters by community; the v2 search does not, so the editor pages (GROWTH-7907 shows the search there) skip the fetch with it.
  useEffect(() => {
    if (!isListingDetailPage || !HEADER_SEARCH_V2_ENABLED) {
      getCommunities();
    }
  }, [getCommunities, isListingDetailPage]);

  const togglePopup = name => {
    if (name === popup) {
      setPopup(false);
    } else {
      setPopup(name);
    }
  };

  return (
    <div className={styles.headerBackground}>
      <header className={cn(styles.headerGlobal, { [styles.headerAccount]: isAccountSettings })}>
        <div className={styles.logoMenuContainer}>
          <Hamburger toggleDropdown={handleOpenNewListing} activeMenu={isActiveMobileMenu} large={isAccountSettings} />
          <div className={styles.logo}>
            <Logo />
          </div>
        </div>

        {HEADER_SEARCH_V2_ENABLED ? (
          <MainSearchV2 className={cn(styles.mainSearch, styles.mainSearchV2)} />
        ) : (
          <MainSearch className={styles.mainSearch} communities={communities} />
        )}

        <div className={styles.headerActions}>
          <LanguageSwitcher />
          <Notification onCloseHandler={() => togglePopup("notification")} popup={popup} />
          <div className="relative">
            <Avatar togglePopup={togglePopup} isOpen={popup === "links"} />
            {popup === "links" && <DetailNavigation onCloseHandler={() => togglePopup("links")} keycloak={keycloak} />}
          </div>
        </div>
      </header>
    </div>
  );
};

export default React.memo(Header);
